
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import CategoryCard from "@/components/category-card";
import FadeInWhenVisible from "./FadeInWhenVisible";
import { categories } from "@/lib/data";
import { StatisticsService } from "@/services/statistics";

const CategoriesSection: React.FC = () => {
  const [updatedCategories, setUpdatedCategories] = useState(categories);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategoryCounts = async () => {
      try {
        setLoading(true);
        const updatedCats = await StatisticsService.categories.updateCategoryCounts(categories);
        setUpdatedCategories(updatedCats);
      } catch (error) {
        console.error("Failed to update category counts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryCounts();
    
    // Refresh category counts every 5 minutes
    const intervalId = setInterval(fetchCategoryCounts, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  return (
    <section className="py-20 bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4">
        <FadeInWhenVisible>
          <div className="text-center mb-12">
            <Badge className="mb-2">Browse by Category</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 dark:text-white">Find the Perfect AI Agent</h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Explore our wide range of categories to find the AI agent that fits your needs
            </p>
          </div>
        </FadeInWhenVisible>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {updatedCategories.slice(0, 8).map((category, index) => (
            <FadeInWhenVisible key={category.id} delay={index * 0.1}>
              {loading ? (
                <div className="h-64 bg-gray-200 dark:bg-gray-800 animate-pulse rounded-lg"></div>
              ) : (
                <CategoryCard category={category} />
              )}
            </FadeInWhenVisible>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button asChild variant="outline" size="lg" className="group">
            <Link to="/categories" className="flex items-center">
              View All Categories
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CategoriesSection;
